import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, Download, FileText, Loader2, Users, Trash2, History, CheckCircle, Mail, Globe, Search, Undo2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import StatementEmailDialog from '../../components/invoices/StatementEmailDialog';
import { calcInvoiceAmount } from '../../utils/invoiceCalc';

const fmt = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

const paymentBadge = {
  paid: 'bg-green-100 text-green-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
};

const StatementsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: eligibleEntities = [] } = useQuery({
    queryKey: QK.statementEligibleEntities(),
    queryFn: () => api.get('/invoices/statement-eligible-entities').then(r => r.data),
  });

  const { data: pastStatements = [], isLoading: loadingHistory } = useQuery({
    queryKey: QK.statements(),
    queryFn: () => api.get('/invoices/statements').then(r => r.data),
  });
  const invalidateStatements = () => qc.invalidateQueries({ queryKey: QK.statements() });

  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvedStmt, setApprovedStmt] = useState(null);
  const [selectedStmt, setSelectedStmt] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailStmt, setEmailStmt] = useState(null);

  const loadInvoices = useCallback((entityId) => {
    if (!entityId) { setInvoices([]); setSelectedIds(new Set()); return; }
    setLoading(true);
    // Read from the existing React Query invoice cache first; fall back to a fresh fetch
    const cached = qc.getQueryData(QK.accountInvoices()) || [];
    const filtered = cached.filter(i =>
      i.crm_entity_id === entityId &&
      ['approved', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status) &&
      i.payment_status === 'unpaid'
    );
    if (filtered.length > 0 || cached.length > 0) {
      // Cache hit — use it immediately
      setInvoices(filtered);
      setSelectedIds(new Set(filtered.map(i => i.id)));
      setLoading(false);
    } else {
      // Cache cold — fetch once and store in cache
      qc.fetchQuery({ queryKey: QK.accountInvoices(), queryFn: () => api.get('/invoices').then(r => r.data) })
        .then(data => {
          const f = (data || []).filter(i =>
            i.crm_entity_id === entityId &&
            ['approved', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status) &&
            i.payment_status === 'unpaid'
          );
          setInvoices(f);
          setSelectedIds(new Set(f.map(i => i.id)));
        })
        .catch(() => toast.error('Failed to load invoices'))
        .finally(() => setLoading(false));
    }
  }, [qc]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEntityChange = (entityId) => {
    setSelectedEntityId(entityId);
    setStatement(null);
    setApprovedStmt(null);
    loadInvoices(entityId);
  };

  const generateStatement = async () => {
    if (!selectedEntityId || selectedIds.size === 0) { toast.error('Select at least one invoice'); return; }
    setGenerating(true);
    setApprovedStmt(null);
    try {
      const res = await api.post('/invoices/generate-statement', {
        crm_entity_id: selectedEntityId,
        invoice_ids: Array.from(selectedIds),
      });
      setStatement(res.data);
      toast.success('Statement preview generated');
    } catch { toast.error('Failed to generate statement'); }
    setGenerating(false);
  };

  const approveStatement = async () => {
    if (!selectedEntityId || selectedIds.size === 0) return;
    setApproving(true);
    try {
      const res = await api.post('/invoices/statements/approve', {
        crm_entity_id: selectedEntityId,
        invoice_ids: Array.from(selectedIds),
      });
      setApprovedStmt(res.data);
      setStatement(null);
      toast.success('Statement approved');
      invalidateStatements();
    } catch { toast.error('Failed to approve statement'); }
    setApproving(false);
  };

  const downloadPdfForStmt = async (stmt) => {
    setDownloading(true);
    try {
      const res = await api.post('/invoices/generate-statement-pdf', {
        crm_entity_id: stmt.crm_entity_id,
        invoice_ids: stmt.invoice_ids || [],
      }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement-${stmt.crm_entity_name || 'owner'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
    setDownloading(false);
  };

  const toggleInvoice = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === invoices.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(invoices.map(i => i.id)));
  };

  const openStmtPreview = async (stmt) => {
    setSelectedStmt(stmt);
    setPdfPreviewUrl(null);
    setPdfLoading(true);
    try {
      const res = await api.post('/invoices/generate-statement-pdf', {
        crm_entity_id: stmt.crm_entity_id,
        invoice_ids: stmt.invoice_ids || [],
      }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      setPdfPreviewUrl(window.URL.createObjectURL(blob));
    } catch { toast.error('Failed to load PDF preview'); }
    setPdfLoading(false);
  };

  const deleteStatement = async (id) => {
    try {
      await api.delete(`/invoices/statements/${id}`);
      toast.success('Statement removed');
      invalidateStatements();
    } catch { toast.error('Failed to delete'); }
  };

  const handlePublishStmt = async (stmtId) => {
    try {
      await api.post(`/invoices/statements/${stmtId}/publish-to-portal`);
      toast.success('Statement published to portal');
      invalidateStatements();
      if (selectedStmt?.id === stmtId) {
        const res = await api.get('/invoices/statements');
        const updated = (res.data || []).find(s => s.id === stmtId);
        if (updated) setSelectedStmt(updated);
      }
      if (approvedStmt?.id === stmtId) {
        setApprovedStmt(prev => ({ ...prev, published_to_portal: true, published_to_portal_at: new Date().toISOString() }));
      }
    } catch { toast.error('Failed to publish'); }
  };

  const handleUnpublishStmt = async (stmtId) => {
    try {
      await api.post(`/invoices/statements/${stmtId}/unpublish-from-portal`);
      toast.success('Statement removed from portal');
      invalidateStatements();
      if (selectedStmt?.id === stmtId) {
        setSelectedStmt(prev => ({ ...prev, published_to_portal: false, published_to_portal_at: null, published_to_portal_by: null }));
      }
      if (approvedStmt?.id === stmtId) {
        setApprovedStmt(prev => ({ ...prev, published_to_portal: false, published_to_portal_at: null, published_to_portal_by: null }));
      }
    } catch { toast.error('Failed to unpublish'); }
  };

  const openEmailDialog = (stmt) => {
    setEmailStmt(stmt);
    setEmailDialogOpen(true);
  };

  // calcInvoiceAmount runs dynamically on the current invoice data from the list
  // endpoint. The backend always live-reads sale_result from the opportunity so
  // this is guaranteed to match the invoice detail page calculation.
  const calcAmount = (inv) => {
    const { amount, isPayable: payable } = calcInvoiceAmount(inv);
    return payable ? amount : -amount;
  };

  const isPayable = (inv) => calcInvoiceAmount(inv).isPayable;

  const selectedInvoices = invoices.filter(i => selectedIds.has(i.id));
  const totalDue     = selectedInvoices.reduce((s, i) => { const { amount, isPayable: p } = calcInvoiceAmount(i); return s + (p ? 0 : Math.abs(amount)); }, 0);
  const totalPayable = selectedInvoices.reduce((s, i) => { const { amount, isPayable: p } = calcInvoiceAmount(i); return s + (p ? amount : 0); }, 0);
  const netBalance = totalPayable - totalDue;
  const selectedEntity = eligibleEntities.find(e => e.id === selectedEntityId);
  const filteredStatements = (() => {
    let list = selectedEntityId
      ? pastStatements.filter(s => s.crm_entity_id === selectedEntityId)
      : pastStatements;
    if (historySearch.trim()) {
      const q = historySearch.toLowerCase();
      list = list.filter(s =>
        (s.crm_entity_name || '').toLowerCase().includes(q) ||
        (s.horse_names || []).some(h => h.toLowerCase().includes(q)) ||
        String(s.invoice_count).includes(q) ||
        new Date(s.generated_at).toLocaleDateString().includes(q)
      );
    }
    return list;
  })();

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} data-testid="back-to-invoices-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Invoices
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-semibold" data-testid="statements-page-title">Statements</h1>
              <p className="text-muted-foreground mt-1">Consolidate multiple invoices into a single statement</p>
            </div>
          </div>
        </div>

        {/* Entity Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div className="flex items-center gap-3 flex-1">
                <div className="w-72">
                  <Select value={selectedEntityId} onValueChange={handleEntityChange} data-testid="entity-select">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Account / Owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleEntities.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">No accounts with 2+ approved unpaid invoices</div>
                      ) : (
                        [...eligibleEntities]
                          .sort((a, b) => {
                            const keyA = a.person_last_name
                              ? `${a.person_last_name} ${a.person_first_name || ''}`.trim()
                              : (a.system_name || '');
                            const keyB = b.person_last_name
                              ? `${b.person_last_name} ${b.person_first_name || ''}`.trim()
                              : (b.system_name || '');
                            return keyA.localeCompare(keyB);
                          })
                          .map(e => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.system_name} ({e.unpaid_invoice_count} invoices)
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {selectedEntityId && !approvedStmt && (
                  <div className="ml-auto">
                    <Button size="sm" variant="outline" onClick={generateStatement} disabled={generating || selectedIds.size === 0} data-testid="generate-statement-btn">
                      {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Preview Statement
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Selection Table */}
        {selectedEntityId && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Approved Unpaid Invoices for {selectedEntity?.system_name}</CardTitle>
                <p className="text-sm text-muted-foreground">{selectedIds.size} of {invoices.length} selected</p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                  <p className="text-muted-foreground">No approved unpaid invoices found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={selectedIds.size === invoices.length && invoices.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                      </TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Horse</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(inv => {
                      const amount = calcAmount(inv);
                      const payable = isPayable(inv);
                      return (
                        <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                          <TableCell>
                            <Checkbox checked={selectedIds.has(inv.id)} onCheckedChange={() => toggleInvoice(inv.id)} data-testid={`invoice-checkbox-${inv.id}`} />
                          </TableCell>
                          <TableCell className="font-medium">{inv.invoice_number_formatted || `#${inv.invoice_number}`}</TableCell>
                          <TableCell>{inv.horse_name || '-'}</TableCell>
                          <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                          <TableCell className={`font-medium ${payable ? 'text-primary' : ''}`}>
                            {payable ? `(${fmt(Math.abs(amount))})` : fmt(Math.abs(amount))}
                          </TableCell>
                          <TableCell><Badge className={paymentBadge[inv.payment_status] || 'bg-gray-100'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Statement Preview (before approval) */}
        {statement && !approvedStmt && (
          <Card data-testid="statement-preview-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Statement Preview</CardTitle>
                <Button size="sm" onClick={approveStatement} disabled={approving} className="bg-primary hover:bg-primary/90 text-white" data-testid="approve-statement-btn">
                  {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Approve Statement
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <StatementPreviewContent statement={statement} calcAmount={calcAmount} isPayable={isPayable} />
            </CardContent>
          </Card>
        )}

        {/* Approved Statement (after approval) */}
        {approvedStmt && (
          <Card data-testid="approved-statement-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Approved Statement</CardTitle>
                  <Badge className="bg-green-100 text-green-700">Approved</Badge>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => downloadPdfForStmt(approvedStmt)} disabled={downloading} className="bg-primary hover:bg-primary/90 text-white" data-testid="download-approved-pdf-btn">
                    {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download PDF
                  </Button>
                  {approvedStmt.emailed ? (
                    <Button size="sm" variant="outline" onClick={() => openEmailDialog(approvedStmt)}><Mail className="w-4 h-4 mr-2" /> Re-send Email</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => openEmailDialog(approvedStmt)} data-testid="email-statement-btn">
                      <Mail className="w-4 h-4 mr-2" /> Email via Green Door
                    </Button>
                  )}
                  {approvedStmt.published_to_portal ? (
                    <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600" onClick={() => handleUnpublishStmt(approvedStmt.id)}><Undo2 className="w-4 h-4 mr-1" /> Undo Publish</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => handlePublishStmt(approvedStmt.id)} data-testid="publish-statement-btn">
                      <Globe className="w-4 h-4 mr-2" /> Publish to Portal
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            {(approvedStmt.emailed || approvedStmt.published_to_portal) && (
              <div className="px-6 pb-2 space-y-1">
                {approvedStmt.emailed && (
                  <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-green-50 border-green-200 w-fit">
                    <Mail className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 font-medium">
                      Emailed {approvedStmt.emailed_to ? `to ${approvedStmt.emailed_to}` : ''} on {approvedStmt.emailed_at ? new Date(approvedStmt.emailed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}{approvedStmt.emailed_by ? ` by ${approvedStmt.emailed_by}` : ''}
                    </span>
                  </div>
                )}
                {approvedStmt.published_to_portal && (
                  <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200 w-fit">
                    <Globe className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 font-medium">
                      Published to Portal on {approvedStmt.published_to_portal_at ? new Date(approvedStmt.published_to_portal_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}{approvedStmt.published_to_portal_by ? ` by ${approvedStmt.published_to_portal_by}` : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 border rounded">
                  <p className="text-sm text-muted-foreground mb-1">Total Due (Expenses)</p>
                  <p className="font-serif text-2xl font-semibold">{fmt(approvedStmt.total_expenses)}</p>
                </div>
                <div className="text-center p-4 border rounded">
                  <p className="text-sm text-muted-foreground mb-1">Total Payable (Proceeds)</p>
                  <p className="font-serif text-2xl font-semibold text-primary">{fmt(approvedStmt.total_proceeds)}</p>
                </div>
                <div className="text-center p-4 border rounded bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-1">Net Balance</p>
                  <p className={`font-serif text-2xl font-bold ${approvedStmt.net_balance >= 0 ? 'text-primary' : 'text-red-600'}`}>
                    {approvedStmt.net_balance >= 0 ? fmt(approvedStmt.net_balance) : `-${fmt(Math.abs(approvedStmt.net_balance))}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{approvedStmt.net_balance >= 0 ? 'Payable to owner' : 'Due from owner'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statement Summary */}
        {selectedEntityId && selectedIds.size > 0 && !statement && !approvedStmt && (
          <Card data-testid="statement-summary-card">
            <CardContent className="p-6">
              <h3 className="font-serif text-lg font-semibold mb-4">Statement Summary</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 border rounded">
                  <p className="text-sm text-muted-foreground mb-1">Total Due (Expenses)</p>
                  <p className="font-serif text-2xl font-semibold" data-testid="total-due">{fmt(totalDue)}</p>
                </div>
                <div className="text-center p-4 border rounded">
                  <p className="text-sm text-muted-foreground mb-1">Total Payable (Proceeds)</p>
                  <p className="font-serif text-2xl font-semibold text-primary" data-testid="total-payable">{fmt(totalPayable)}</p>
                </div>
                <div className="text-center p-4 border rounded bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-1">Net Balance</p>
                  <p className={`font-serif text-2xl font-bold ${netBalance >= 0 ? 'text-primary' : 'text-red-600'}`} data-testid="net-balance">
                    {netBalance >= 0 ? fmt(netBalance) : `-${fmt(Math.abs(netBalance))}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{netBalance >= 0 ? 'Payable to owner' : 'Due from owner'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previously Generated Statements */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-base">Previously Generated Statements</CardTitle>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-9 h-8 text-sm" placeholder="Search statements..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} data-testid="history-search-input" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingHistory ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : filteredStatements.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/20" />
                <p className="text-sm text-muted-foreground">{historySearch ? 'No matching statements' : selectedEntityId ? 'No statements for this account' : 'No statements generated yet'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account / Owner</TableHead>
                    <TableHead>Horses</TableHead>
                    <TableHead>Invoices</TableHead>
                    <TableHead>Expenses</TableHead>
                    <TableHead>Proceeds</TableHead>
                    <TableHead>Net Balance</TableHead>
                    <TableHead>Generated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStatements.map(stmt => (
                    <TableRow key={stmt.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/invoices/statements/${stmt.id}`)} data-testid={`statement-row-${stmt.id}`}>
                      <TableCell className="font-medium">{stmt.crm_entity_name}</TableCell>
                      <TableCell className="text-sm">{(stmt.horse_names || []).join(', ') || '-'}</TableCell>
                      <TableCell><Badge variant="outline">{stmt.invoice_count}</Badge></TableCell>
                      <TableCell>{fmt(stmt.total_expenses)}</TableCell>
                      <TableCell className="text-primary">{fmt(stmt.total_proceeds)}</TableCell>
                      <TableCell className={`font-medium ${stmt.net_balance >= 0 ? 'text-primary' : 'text-red-600'}`}>
                        {stmt.net_balance >= 0 ? fmt(stmt.net_balance) : `-${fmt(Math.abs(stmt.net_balance))}`}
                      </TableCell>
                      <TableCell className="text-sm">{new Date(stmt.generated_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Statement PDF Preview Dialog */}
      <Dialog open={!!selectedStmt} onOpenChange={(open) => { if (!open) { if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl); setSelectedStmt(null); setPdfPreviewUrl(null); } }}>
        <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Statement - {selectedStmt?.crm_entity_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : pdfPreviewUrl ? (
              <iframe src={pdfPreviewUrl} className="w-full h-full border rounded" title="Statement PDF Preview" />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">Failed to load preview</div>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={() => { deleteStatement(selectedStmt.id); if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl); setSelectedStmt(null); setPdfPreviewUrl(null); }} data-testid="dialog-delete-stmt-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => downloadPdfForStmt(selectedStmt)} disabled={downloading} className="bg-primary hover:bg-primary/90 text-white" data-testid="dialog-download-stmt-btn">
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
              {selectedStmt?.emailed ? (
                <Button size="sm" variant="outline" onClick={() => { openEmailDialog(selectedStmt); setSelectedStmt(null); }} data-testid="dialog-email-stmt-btn">
                  <Mail className="w-4 h-4 mr-2" /> Re-send Email
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => { openEmailDialog(selectedStmt); setSelectedStmt(null); }} data-testid="dialog-email-stmt-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email via Green Door
                </Button>
              )}
              {selectedStmt?.published_to_portal ? (
                <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600" onClick={() => handleUnpublishStmt(selectedStmt.id)} data-testid="dialog-unpublish-stmt-btn">
                  <Undo2 className="w-4 h-4 mr-1" /> Undo Publish
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => handlePublishStmt(selectedStmt.id)} data-testid="dialog-publish-stmt-btn">
                  <Globe className="w-4 h-4 mr-2" /> Publish to Portal
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Statement Email Dialog */}
      <StatementEmailDialog
        open={emailDialogOpen}
        onClose={(sent) => {
          setEmailDialogOpen(false);
          if (sent) invalidateStatements();
        }}
        statementId={emailStmt?.id}
        entityName={emailStmt?.crm_entity_name}
      />
    </SidebarLayout>
  );
};

const StatementPreviewContent = ({ statement, calcAmount, isPayable }) => (
  <div className="space-y-4">
    <div className="flex justify-between border-b pb-3">
      <div>
        <p className="text-xs text-muted-foreground font-semibold">STATEMENT FOR</p>
        <p className="font-semibold">{statement.crm_entity_name}</p>
      </div>
      <div className="text-right text-sm">
        <p className="text-muted-foreground">Generated: {new Date(statement.generated_at).toLocaleDateString()}</p>
        <p className="text-muted-foreground">{statement.invoice_count} invoice(s)</p>
      </div>
    </div>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Invoice #</TableHead>
          <TableHead>Horse</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(statement.invoices || []).map(inv => {
          const amount = calcAmount(inv);
          const payable = isPayable(inv);
          return (
            <TableRow key={inv.id}>
              <TableCell className="font-medium">{inv.invoice_number_formatted}</TableCell>
              <TableCell>{inv.horse_name || '-'}</TableCell>
              <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
              <TableCell><Badge className={paymentBadge[inv.payment_status] || 'bg-gray-100'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
              <TableCell className={`text-right font-medium ${payable ? 'text-primary' : ''}`}>
                {payable ? `(${fmt(Math.abs(amount))})` : fmt(Math.abs(amount))}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    <div className="border-t pt-3 space-y-1 text-sm">
      <div className="flex justify-end gap-8"><span className="text-muted-foreground">Total Expenses</span><span className="font-medium w-28 text-right">{fmt(statement.total_expenses)}</span></div>
      <div className="flex justify-end gap-8"><span className="text-muted-foreground">Total Sale Proceeds</span><span className="font-medium w-28 text-right text-primary">{fmt(statement.total_sale_proceeds)}</span></div>
      <div className="flex justify-end gap-8"><span className="text-muted-foreground">Total Paid</span><span className="font-medium w-28 text-right">{fmt(statement.total_paid)}</span></div>
      <div className="flex justify-end gap-8 font-semibold text-base">
        <span>{(statement.total_unpaid ?? 0) < 0 ? 'Payable to Owner' : 'Outstanding'}</span>
        <span className={`w-28 text-right ${(statement.total_unpaid ?? 0) < 0 ? 'text-primary' : ''}`}>
          {(statement.total_unpaid ?? 0) < 0
            ? `(${fmt(Math.abs(statement.total_unpaid))})`
            : fmt(statement.total_unpaid ?? 0)}
        </span>
      </div>
    </div>
  </div>
);

export default StatementsPage;
